import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

const TvaDropdown = ({
  tvaRates,
  tvaId,
  setTvaId,
  isLoadingTva,
  tvaError
}) => {
  const [isTvaDropdownOpen, setIsTvaDropdownOpen] = useState(false);
  const [tvaSearchTerm, setTvaSearchTerm] = useState('');
  const tvaDropdownRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  // Update dropdown position when it's opened or when scrolling
  useEffect(() => {
    const updatePosition = () => {
      if (tvaDropdownRef.current && isTvaDropdownOpen) {
        const rect = tvaDropdownRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom,
          left: rect.left,
          width: rect.width
        });
      }
    };

    updatePosition();

    // Add scroll event listener
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isTvaDropdownOpen]);

  const selectedTva = tvaRates.find(t => t.id === tvaId);

  // Filter TVA rates based on search term
  const filteredTvaRates = tvaRates.filter(tva =>
    tva.taux.toString().includes(tvaSearchTerm)
  );

  const handleSelectTva = (id) => {
    setTvaId(id);
    setIsTvaDropdownOpen(false);
  };

  // Add this function to clear the selected TVA
  const handleClearTva = (e) => {
    e.stopPropagation(); // Prevent dropdown from opening
    setTvaId(null); // Clear the selected TVA
  };

  return (
    <div className="sm:col-span-2">
      <label htmlFor="tva" className="block text-sm font-medium text-gray-700 mb-1">
        Taux de TVA <span className="text-red-500">*</span>
      </label>
      <div className="relative" ref={tvaDropdownRef}>
        <button
          type="button"
          className="bg-white relative w-full border border-gray-300 rounded-md shadow-sm pl-3 pr-10 py-2 text-left cursor-default focus:outline-none focus:ring-1 focus:ring-[#174540] focus:border-[#174540] sm:text-sm"
          onClick={() => setIsTvaDropdownOpen(!isTvaDropdownOpen)}
        >
          <span className="block truncate">
            {selectedTva ? `${selectedTva.taux}%` : 'Sélectionner un taux'}
          </span>
          <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3z" clipRule="evenodd" />
              <path fillRule="evenodd" d="M10 17a1 1 0 01-.707-.293l-3-3a1 1 0 011.414-1.414L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3A1 1 0 0110 17z" clipRule="evenodd" />
            </svg>
          </span>
        </button>
        
        {/* Add clear button when TVA is selected */}
        {selectedTva && (
          <div 
            className="absolute inset-y-0 right-0 flex items-center pr-8 z-10 cursor-pointer"
            onClick={handleClearTva}
          >
            <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </div>
        )}

        {isTvaDropdownOpen && createPortal(
          <div
            className="fixed z-50 bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm"
            style={{
              width: dropdownPosition.width,
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '320px'
            }}
          >
            <div className="sticky top-0 z-10 bg-white">
              <input
                type="text"
                className="block w-full px-4 py-2 text-sm border-0 focus:ring-0 focus:outline-none"
                placeholder="Rechercher un taux de TVA..."
                value={tvaSearchTerm}
                onChange={(e) => {
                  e.stopPropagation();
                  setTvaSearchTerm(e.target.value);
                }}
                onClick={(e) => e.stopPropagation()}
              />
              <div className="h-px bg-gray-200" />
            </div>

            {isLoadingTva ? (
              <div className="py-3 px-3 text-sm text-gray-500 flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Chargement des taux de TVA...
              </div>
            ) : tvaError ? (
              <div className="py-2 px-3 text-sm text-red-500">{tvaError}</div>
            ) : filteredTvaRates.length === 0 ? (
              <div className="py-2 px-3 text-sm text-gray-500">Aucun taux de TVA trouvé</div>
            ) : (
              filteredTvaRates.map((tva) => (
                <div
                  key={tva.id}
                  className={`cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-100 ${tvaId === tva.id ? 'bg-[#174540] bg-opacity-10 font-medium' : ''
                    }`}
                  onClick={() => handleSelectTva(tva.id)}
                >
                  <div className="flex items-center">
                    <span className="font-medium text-gray-900">{tva.taux}%</span>
                  </div>

                  {tvaId === tva.id && (
                    <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-[#174540]">
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </span>
                  )}
                </div>
              ))
            )}
          </div>,
          document.body
        )}
      </div>
    </div>
  );
};

export default TvaDropdown;